import { h } from '@/lib/dom';
import { formatNumber } from '@/lib/format';

export interface PhaseSegment {
  key: 'deep' | 'rem' | 'light' | 'awake';
  label: string;
  /** Percentage 0..100 */
  pct: number | null;
  /** Absolute minutes for tooltip / caption */
  minutes: number | null;
  color: string;
}

export interface PhasesBarProps {
  segments: PhaseSegment[];
  /** Accessible label for the whole bar (screen readers). */
  ariaLabel?: string;
  /** Show legend with % + absolute minutes below the bar (default true). */
  showCaption?: boolean;
}

/**
 * Horizontal stacked bar visualizing sleep phases.
 * Colors are passed in by the caller (tokens) — no color logic inside.
 * Accessibility: the whole bar is role="img" with an aria-label summarizing
 * percentages; the caption is role="list" with per-phase items.
 */
export function PhasesBar(props: PhasesBarProps): HTMLElement {
  const segs = props.segments;
  const segsWithValue = segs.filter((s): s is PhaseSegment & { pct: number } =>
    s.pct != null && Number.isFinite(s.pct));

  const aria = props.ariaLabel
    ?? segsWithValue.map((s) => `${s.label} ${formatNumber(s.pct)}%`).join(', ');

  const bar = h('div', {
    class: 'phases-bar',
    role: 'img',
    aria: { label: aria },
    style: [
      'display: flex',
      'width: 100%',
      'height: 20px',
      'border-radius: var(--radius-sm)',
      'overflow: hidden',
      'background: var(--bg-surface-2)',
      'border: 1px solid var(--border-default)',
    ].join(';'),
  });

  if (!segsWithValue.length) {
    bar.appendChild(h('div', {
      style: 'flex: 1; display: grid; place-items: center; color: var(--text-tertiary); font-size: 12px;',
    }, 'Нет данных'));
  } else {
    for (const s of segsWithValue) {
      const segEl = h('div', {
        title: `${s.label}: ${formatNumber(s.pct)}%${s.minutes != null ? ` · ${formatNumber(s.minutes, 0)} мин` : ''}`,
        style: `flex: ${s.pct}; background: ${s.color}; min-width: 1px;`,
      });
      bar.appendChild(segEl);
    }
  }

  if (props.showCaption === false) return bar;

  const caption = h('ul', { class: 'phases-caption', role: 'list' });

  for (const s of segs) {
    const pctStr = s.pct != null ? `${formatNumber(s.pct, 1)}%` : '—';
    const minStr = s.minutes != null ? `${formatNumber(s.minutes, 0)} мин` : '—';
    caption.appendChild(h('li', { class: 'phases-caption-row' }, [
      h('span', {
        class: 'phases-caption-dot',
        'aria-hidden': 'true',
        style: `background:${s.color};`,
      }),
      h('span', { class: 'phases-caption-label' }, s.label),
      h('span', { class: 'phases-caption-pct tabular' }, pctStr),
      h('span', { class: 'phases-caption-min tabular' }, minStr),
    ]));
  }

  return h('div', { class: 'phases-block' }, [bar, caption]);
}

export const PHASE_COLORS = {
  deep: '#0F4F9D', // deep — сильнее primary
  rem: '#1A56DB', // rem — sync c chart-point-below
  light: '#8DBCE8', // light — primary-border
  awake: '#CBD2D9', // awake — border-strong
} as const;
